import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ComposedChart,
  Area,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { formatCurrency, formatPercentage, formatNumber } from '@/lib/utils/format';
import type { TrendEntry } from '../types/analytics.types';

const tooltipStyle = {
  backgroundColor: 'hsl(var(--card))',
  border: '1px solid hsl(var(--border))',
  borderRadius: '8px',
  color: 'hsl(var(--foreground))',
};

function formatMonthLabel(month: string): string {
  const [year, m] = month.split('-');
  const months = [
    'Ene',
    'Feb',
    'Mar',
    'Abr',
    'May',
    'Jun',
    'Jul',
    'Ago',
    'Sep',
    'Oct',
    'Nov',
    'Dic',
  ];
  return `${months[parseInt(m, 10) - 1]} ${year.slice(2)}`;
}

interface TrendsTabProps {
  data: TrendEntry[] | null;
  isLoading: boolean;
}

export function TrendsTab({ data, isLoading }: TrendsTabProps) {
  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <Skeleton className="h-[400px] w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Tendencias</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-32 flex items-center justify-center text-muted-foreground text-sm">
            Sin datos de tendencias
          </div>
        </CardContent>
      </Card>
    );
  }

  const chartData = data.map((d) => ({
    ...d,
    label: formatMonthLabel(d.month),
  }));

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Tendencias Mensuales</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[400px]">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData}>
              <defs>
                <linearGradient id="trendRevenueGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} className="fill-muted-foreground" />
              <YAxis
                yAxisId="left"
                tick={{ fontSize: 12 }}
                className="fill-muted-foreground"
                tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ fontSize: 12 }}
                className="fill-muted-foreground"
                tickFormatter={(v) => `${v.toFixed(0)}%`}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null;
                  const entry = payload[0].payload;
                  return (
                    <div style={tooltipStyle} className="p-3 shadow-lg">
                      <p className="font-medium text-sm mb-1">{label}</p>
                      <p className="text-sm text-blue-500">
                        Ingresos: {formatCurrency(entry.revenue)}
                      </p>
                      <p className="text-sm text-slate-500">Costo: {formatCurrency(entry.cost)}</p>
                      <p className="text-sm text-green-500">
                        Margen: {formatPercentage(entry.avgMargin)}
                      </p>
                      <p className="text-sm text-violet-500">
                        Cotizaciones: {formatNumber(entry.quoteCount, 0)}
                      </p>
                    </div>
                  );
                }}
              />
              <Legend
                formatter={(value: string) => {
                  const labels: Record<string, string> = {
                    revenue: 'Ingresos',
                    avgMargin: 'Margen %',
                    quoteCount: 'Cotizaciones',
                  };
                  return labels[value] || value;
                }}
              />
              <Area
                yAxisId="left"
                type="monotone"
                dataKey="revenue"
                stroke="#3b82f6"
                strokeWidth={2}
                fill="url(#trendRevenueGradient)"
                name="revenue"
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="avgMargin"
                stroke="#10b981"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={{ fill: '#10b981', r: 4 }}
                name="avgMargin"
              />
              <Bar
                yAxisId="left"
                dataKey="quoteCount"
                fill="#8b5cf6"
                fillOpacity={0.3}
                radius={[4, 4, 0, 0]}
                name="quoteCount"
                barSize={20}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
